import { PixelCanvas } from './pixel.js';

/**
 * Turns a low-resolution pixel buffer into a smooth, high-resolution image.
 *
 * Used for art that is authored as a grid of hand-placed pixels (the UI
 * icons), which cannot be redrawn as vector shapes: each colour region is
 * treated as a shape: for every output pixel we take a small Gaussian-weighted
 * vote among the nearby source pixels and pick the winning colour, then
 * anti-alias against the runner-up by how narrow the win was. Straight edges
 * stay straight, stair-steps become clean diagonals and curves, and corners
 * are gently rounded. Flat areas are copied straight through, so big rooms
 * stay cheap.
 */

export interface SmoothOptions {
  /** Gaussian width in source pixels; larger rounds corners more. */
  sigma?: number;
  /** Edge softness in output pixels; 1 is a crisp anti-aliased edge. */
  softness?: number;
  /** Extra weight for a pixel's own colour, which keeps one-pixel details alive. */
  center?: number;
  /** Extra weight for very dark pixels, which keeps outlines unbroken. */
  ink?: number;
}

const RADIUS = 1; // neighbourhood is (2 * RADIUS + 1)^2 source pixels

function premultiply(packed: number): [number, number, number, number] {
  const a = packed >>> 24;
  if (a === 0) return [0, 0, 0, 0];
  const f = a / 255;
  return [(packed & 255) * f, ((packed >>> 8) & 255) * f, ((packed >>> 16) & 255) * f, a];
}

/**
 * `src` holds ABGR pixels (ImageData byte order). Returns a new buffer that
 * is `scale` times larger in each direction.
 */
export function smoothUpscale(
  src: Uint32Array,
  width: number,
  height: number,
  scale: number,
  options: SmoothOptions = {},
): Uint32Array {
  const sigma = options.sigma ?? 0.5;
  const softness = options.softness ?? 1;
  const center = options.center ?? 0;
  const ink = options.ink ?? 0;
  const outW = width * scale;
  const out = new Uint32Array(outW * height * scale);
  const taps = (2 * RADIUS + 1) ** 2;

  // Kernel weight per phase (output pixel within its source pixel) and tap.
  const g = (d: number) => Math.exp(-(d * d) / (2 * sigma * sigma));
  const kernel = new Float32Array(scale * scale * taps);
  const kernelSum = new Float32Array(scale * scale);
  for (let py = 0; py < scale; py += 1) {
    for (let px = 0; px < scale; px += 1) {
      const dx0 = (px + 0.5) / scale - 0.5;
      const dy0 = (py + 0.5) / scale - 0.5;
      let sum = 0;
      let t = 0;
      for (let ny = -RADIUS; ny <= RADIUS; ny += 1) {
        for (let nx = -RADIUS; nx <= RADIUS; nx += 1) {
          const w = g(dx0 - nx) * g(dy0 - ny) * (nx === 0 && ny === 0 ? 1 + center : 1);
          kernel[(py * scale + px) * taps + t] = w;
          sum += w;
          t += 1;
        }
      }
      kernelSum[py * scale + px] = sum;
    }
  }
  // How fast the (normalised) vote margin grows per output pixel at a straight
  // edge, so `softness` can be expressed in output pixels.
  const slopePerSource = 2 / (sigma * Math.sqrt(2 * Math.PI));
  const gain = scale / (2 * slopePerSource * softness);

  const colors = new Uint32Array(taps);
  const distinct: number[] = [];
  const tapIndex = new Int8Array(taps);
  const votes = new Float32Array(taps);
  const inkBoost = new Float32Array(taps);

  const colorAt = (x: number, y: number): number => {
    if (x < 0 || y < 0 || x >= width || y >= height) return 0;
    const v = src[y * width + x]!;
    return v >>> 24 === 0 ? 0 : v;
  };

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let t = 0;
      let flat = true;
      const first = colorAt(x, y);
      for (let ny = -RADIUS; ny <= RADIUS; ny += 1) {
        for (let nx = -RADIUS; nx <= RADIUS; nx += 1) {
          const c = colorAt(x + nx, y + ny);
          colors[t] = c;
          if (c !== first) flat = false;
          t += 1;
        }
      }

      const baseX = x * scale;
      const baseY = y * scale;
      if (flat) {
        for (let py = 0; py < scale; py += 1) {
          const row = (baseY + py) * outW + baseX;
          for (let px = 0; px < scale; px += 1) out[row + px] = first;
        }
        continue;
      }

      distinct.length = 0;
      for (let i = 0; i < taps; i += 1) {
        let d = distinct.indexOf(colors[i]!);
        if (d < 0) {
          d = distinct.length;
          distinct.push(colors[i]!);
        }
        tapIndex[i] = d;
      }
      for (let d = 0; d < distinct.length; d += 1) {
        const c = distinct[d]!;
        const lum = c === 0 ? 1 : ((c & 255) * 0.3 + ((c >>> 8) & 255) * 0.59 + ((c >>> 16) & 255) * 0.11) / 255;
        inkBoost[d] = c !== 0 && lum < 0.28 ? 1 + ink : 1;
      }

      for (let py = 0; py < scale; py += 1) {
        for (let px = 0; px < scale; px += 1) {
          const phase = (py * scale + px) * taps;
          votes.fill(0, 0, distinct.length);
          for (let i = 0; i < taps; i += 1) votes[tapIndex[i]!]! += kernel[phase + i]!;
          if (ink > 0) for (let d = 0; d < distinct.length; d += 1) votes[d]! *= inkBoost[d]!;

          let best = 0;
          let second = -1;
          for (let d = 1; d < distinct.length; d += 1) {
            if (votes[d]! > votes[best]!) {
              second = best;
              best = d;
            } else if (second < 0 || votes[d]! > votes[second]!) second = d;
          }

          let result = distinct[best]!;
          if (second >= 0) {
            const margin = (votes[best]! - votes[second]!) / kernelSum[py * scale + px]!;
            const t2 = Math.min(1, Math.max(0, 0.5 + margin * gain));
            if (t2 < 1) {
              const a = premultiply(distinct[best]!);
              const b = premultiply(distinct[second]!);
              const alpha = a[3] * t2 + b[3] * (1 - t2);
              if (alpha < 0.5) result = 0;
              else {
                const k = 255 / alpha;
                const r = Math.round((a[0] * t2 + b[0] * (1 - t2)) * k);
                const gg = Math.round((a[1] * t2 + b[1] * (1 - t2)) * k);
                const bb = Math.round((a[2] * t2 + b[2] * (1 - t2)) * k);
                result =
                  ((Math.round(alpha) << 24) |
                    (Math.min(255, bb) << 16) |
                    (Math.min(255, gg) << 8) |
                    Math.min(255, r)) >>>
                  0;
              }
            }
          }
          out[(baseY + py) * outW + baseX + px] = result;
        }
      }
    }
  }
  return out;
}

/**
 * Scale2x (EPX): doubles a picture, turning one-pixel stair-steps into clean
 * diagonals without inventing any new colours.
 */
function scale2x(src: Uint32Array, width: number, height: number): Uint32Array {
  const out = new Uint32Array(width * 2 * height * 2);
  const at = (x: number, y: number, fallback: number) =>
    x < 0 || y < 0 || x >= width || y >= height ? fallback : src[y * width + x]!;
  const norm = (v: number) => (v >>> 24 === 0 ? 0 : v);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const p = norm(src[y * width + x]!);
      const a = norm(at(x, y - 1, 0));
      const b = norm(at(x + 1, y, 0));
      const c = norm(at(x - 1, y, 0));
      const d = norm(at(x, y + 1, 0));
      const e0 = c === a && c !== d && a !== b ? a : p;
      const e1 = a === b && a !== c && b !== d ? b : p;
      const e2 = d === c && d !== b && c !== a ? c : p;
      const e3 = b === d && b !== a && d !== c ? d : p;
      const o = y * 2 * width * 2 + x * 2;
      out[o] = e0;
      out[o + 1] = e1;
      out[o + width * 2] = e2;
      out[o + width * 2 + 1] = e3;
    }
  }
  return out;
}

/** Whether `scale` can be reached by doubling from 1. */
const isPowerOfTwo = (n: number) => n >= 1 && (n & (n - 1)) === 0;

/** The smoothing that suits art scaled up by `scale`. */
function defaultOptions(scale: number): SmoothOptions {
  return { sigma: scale * 0.19, softness: 1.4, center: 0.5, ink: 0.2 };
}

/**
 * Smooth a scale-1 canvas of hand-placed pixels into a canvas `scale` times
 * denser: diagonals are cleaned up by doubling passes, then edges are rounded
 * and anti-aliased. `scale` must be a power of two.
 */
export function smoothPixels(pc: PixelCanvas, scale: number, options?: SmoothOptions): PixelCanvas {
  let data = pc.data;
  let w = pc.bufWidth;
  let h = pc.bufHeight;
  for (let step = 1; step < scale; step *= 2) {
    data = scale2x(data, w, h);
    w *= 2;
    h *= 2;
  }
  const out = new PixelCanvas(pc.width, pc.height, scale);
  out.data.set(smoothUpscale(data, w, h, 1, options ?? defaultOptions(scale)));
  return out;
}

/** `smoothPixels`, as a canvas element, for icons shown in the page. */
export function smoothToCanvas(pc: PixelCanvas, scale: number, options?: SmoothOptions): HTMLCanvasElement {
  return smoothPixels(pc, scale, options).toCanvas();
}

/**
 * Paints hand-placed pixel art (rows of characters) onto `pc` at (x, y) as
 * smooth shapes instead of square blocks. `colorFor` maps a character to a
 * colour; characters it returns nothing for are transparent.
 */
export function stamp(
  pc: PixelCanvas,
  rows: readonly string[],
  colorFor: (ch: string) => string | undefined,
  x: number,
  y: number,
): void {
  const width = Math.max(...rows.map((r) => r.length));
  const src = new PixelCanvas(width, rows.length, 1);
  rows.forEach((row, ry) => {
    [...row].forEach((ch, rx) => {
      const color = colorFor(ch);
      if (color) src.px(rx, ry, color);
    });
  });
  if (isPowerOfTwo(pc.scale) && pc.scale > 1) {
    pc.blit(smoothPixels(src, pc.scale), x, y);
    return;
  }
  // Sparse buffers (big rooms) cannot use the doubling passes; paint blocks.
  rows.forEach((row, ry) => {
    [...row].forEach((ch, rx) => {
      const color = colorFor(ch);
      if (color) pc.px(x + rx, y + ry, color);
    });
  });
}
